import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import isNil from 'lodash/isNil';
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import { distinctUntilKeyChanged, filter, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { MessagePayload } from '../../../../interfaces/messages/message-payload.interface';
import { MessageThreadDetails } from '../../../../interfaces/messages/message-thread-details.interface';
import { ShipmentOrderPerson } from '../../../../interfaces/shipment-order-person.interface';
import { TeamMemberLists } from '../../../../interfaces/team-member-lists.interface';
import { TeamMemberService } from '../../../../services/team-member.service';
import { DIALOG_DATA } from '../../../dialog/dialog.tokens';
import { DialogData } from '../../../dialog/interfaces/dialog-data.interface';
import { ChannelAndMessage, SendbirdService } from '../../../message-thread/services/sendbird-message.service';
import { MessageDialogDataService } from '../../services/message-dialog-data.service';
import { MessageDialogPayload } from '../../types/message-dialog-payload.interface';
import { SendbirdMessageThreadComponent } from '../../../message-thread/components/sendbird-message-thread/sendbird-message-thread.component';
import { LoadingIndicatorComponent } from '../../../loading-indicator/components/loading-indicator/loading-indicator.component';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-message-dialog',
  templateUrl: './message-dialog.component.html',
  styleUrls: ['./message-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, LoadingIndicatorComponent, SendbirdMessageThreadComponent],
})
export class MessageDialogComponent implements OnInit, OnDestroy {
  // tslint:disable-next-line: variable-name
  private readonly _messageThreadDetails$ = new BehaviorSubject<MessageThreadDetails | undefined>(undefined);
  private readonly _channelAndMessage$ = new BehaviorSubject<ChannelAndMessage | undefined>(undefined);

  public get messageThreadDetails(): MessageThreadDetails | undefined {
    return this._messageThreadDetails$.getValue();
  }
  public set messageThreadDetails(messageThreadDetails: MessageThreadDetails) {
    this._messageThreadDetails$.next(messageThreadDetails);
  }

  public get channelAndMessage(): ChannelAndMessage | undefined {
    return this._channelAndMessage$.getValue();
  }
  public set channelAndMessage(channelAndMessage: ChannelAndMessage) {
    this._channelAndMessage$.next(channelAndMessage);
  }

  public teamMembers: TeamMemberLists = {
    taggable: [],
    mentionable: [],
  };
  public shipmentOrderPeople: ShipmentOrderPerson[] = [];
  public isLoading = false;
  public isSending = false;

  private readonly destroyed$ = new Subject<void>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() public S3fileMessageDownload = new EventEmitter<any>();

  constructor(
    @Inject(DIALOG_DATA) public readonly data: DialogData<MessageDialogPayload, MessageThreadDetails>,
    private readonly messageDialogDataService: MessageDialogDataService,
    private readonly cdr: ChangeDetectorRef,
    private readonly teamMemberService: TeamMemberService,
    private readonly sendbirdSerive: SendbirdService
  ) {}

  public ngOnInit(): void {
    this.loadInitialData();
    this.startCaseDetailsPoll();
  }

  private loadInitialData(): void {
    this.isLoading = true;
    forkJoin([
      this.messageDialogDataService.getInitialMessageThread$(this.data.payload),
      this.messageDialogDataService.getMessageThreadSendbird$(this.data.payload.id, this.data.payload),
      this.messageDialogDataService.getTeamMembers$(this.data.payload.shipment?.id, this.data.payload.teamMemberListType),
      this.teamMemberService.getShipmentOrderPeople$(this.data.payload.shipment?.id),
    ])
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(([messageThreadDetails, channelandmembers, teamMembers, shipmentOrderPeople]) => {
        this.messageThreadDetails = messageThreadDetails;
        this.channelAndMessage = channelandmembers;
        this.teamMembers = teamMembers;
        this.shipmentOrderPeople = shipmentOrderPeople;
        this.cdr.markForCheck();
      });
    this.registerEventHandlers();
  }

  private registerEventHandlers(): void {
    this.sendbirdSerive.registerEventHandlers((data: { event: string; data: any }) => {
      if (
        this.channelAndMessage.channel?.url &&
        data.event === 'onMessageReceived' &&
        data.data.channel.url === this.channelAndMessage.channel.url
      ) {
        this.channelAndMessage.messages.push(data.data.message);
        this.cdr.markForCheck();
      }
    });
  }

  private startCaseDetailsPoll(): void {
    this._messageThreadDetails$
      .pipe(
        filter((messageThreadDetails) => !isNil(messageThreadDetails?.id)),
        distinctUntilKeyChanged('id'),
        switchMap((messageThreadDetails) => this.messageDialogDataService.pollMessageThread$(messageThreadDetails)),
        takeUntil(this.destroyed$)
      )
      .subscribe((messageThreadDetails) => {
        this.messageThreadDetails = messageThreadDetails;
        this.cdr.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onMessageSend(messagePayload: MessagePayload): void {
    this.isSending = true;
  }
  public downloadS3Message(event: { objectKey: string }): void {
    this.sendbirdSerive.downloadS3Message(event);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public onOutSideClick(value: any): void {
    this.data.dialogRef.close(value);
  }
}
